import { useState, useEffect, useCallback } from 'react'
import Toast, { useToast } from '../../components/Toast';
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { parseISO, formatDistanceToNow } from 'date-fns'

// ── AGENT METADATA ────────────────────────────────────────────────────────────
// Static descriptions and invocable function names per agent.
// reconciliation-agent was retired on 2026-10-19: its checks live in
// pre-flight, the run self-audit and the Daily Digest. Its past reports stay
// in reconciliation_reports.

const AGENT_META = {
  'compliance-dashboard-agent': {
    label:       'Compliance Dashboard',
    description: 'Scores each active client on eligibility, supervisor visits, cert status, and prior auth. Builds weekly compliance report.',
    icon:        '✅',
    invocable:   false,
  },
  'prior-auth-renewal-agent': {
    label:       'Prior Auth Renewal',
    description: 'Scans upcoming PA expirations (30/14/7 days) and drafts renewal letters for coordinator review.',
    icon:        '📋',
    invocable:   false,
  },
  'staff-onboarding-agent': {
    label:       'Staff Onboarding',
    description: 'Monitors new hire document status. Sends reminder emails for missing items (CPR card, TB test, background check).',
    icon:        '👤',
    invocable:   false,
  },
  'gapp-doc-generator-agent': {
    label:       'GAPP Doc Generator',
    description: 'Generates GAPP appendix packets and prior auth letters from assessment data on demand.',
    icon:        '📄',
    invocable:   false,
  },
  'intake-auth-agent': {
    label:       'Intake Auth Check',
    description: 'Verifies Medicaid eligibility and existing authorizations when a new client enters the intake pipeline.',
    icon:        '🔍',
    invocable:   false,
  },
  'caregiver-match-agent': {
    label:       'Caregiver Matching',
    description: 'Suggests best-fit caregivers for open client slots based on skills, location, and availability. Blocked: requires expanded AxisCare API access.',
    icon:        '🔗',
    invocable:   false,
  },
  'care-notes-audit-agent': {
    label:       'Care Notes Audit',
    description: 'Reviews weekly visit notes for completeness and flags entries missing required Medicaid documentation elements.',
    icon:        '📝',
    invocable:   false,
  },
  'portal-assistant-agent': {
    label:       'Portal Assistant',
    description: 'AI-powered assistant in the family portal for answering schedule, visit, and billing questions. Deferred pending portal SaaS decision.',
    icon:        '💬',
    invocable:   false,
  },
}

const SCHEDULE_LABELS = {
  '0 7 * * *':  'Nightly · 2am ET',
  '0 11 * * 1': 'Mondays · 6am ET',
  '0 13 * * *': 'Daily · 8am ET',
  '0 12 * * *': 'Daily · 7am ET',
  '0 11 * * 5': 'Fridays · 6am ET',
}

function scheduleLabel(cron) {
  return cron ? (SCHEDULE_LABELS[cron] || cron) : 'Manual trigger'
}

// ── STATUS BADGE ──────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = {
    success: { label: 'Success',  bg: 'var(--success-bg)', color: 'var(--success)', border: 'var(--success-border)' },
    error:   { label: 'Error',    bg: 'var(--danger-bg)',  color: 'var(--danger)',  border: 'var(--danger-border)'  },
    running: { label: 'Running…', bg: 'var(--info-bg)',    color: 'var(--info)',    border: 'var(--info-border)'    },
    waiting: { label: 'Waiting',  bg: 'var(--bg2)',                 color: 'var(--text3)',             border: 'var(--border)'                   },
  }[status] || { label: 'Never run', bg: 'var(--bg2)', color: 'var(--text3)', border: 'var(--border)' }

  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
      background: cfg.bg, color: cfg.color, border: `0.5px solid ${cfg.border}`,
      textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>
      {cfg.label}
    </span>
  )
}

// ── AGENT CARD ────────────────────────────────────────────────────────────────

function AgentCard({ config, onToggle, onRunNow, runningId }) {
  const meta     = AGENT_META[config.agent_id] || {}
  const isRunning = runningId === config.agent_id
  const canRun   = meta.invocable && config.enabled

  return (
    <div style={{
      background: 'var(--bg)',
      border: `0.5px solid ${config.enabled ? 'var(--border)' : 'var(--border)'}`,
      borderRadius: 10,
      padding: '16px 18px',
      opacity: config.enabled ? 1 : 0.6,
      transition: 'opacity 0.2s',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8, flexShrink: 0,
            background: 'var(--bg2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16,
          }}>
            {meta.icon || '🤖'}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
              {meta.label || config.agent_id}
            </div>
            <StatusBadge status={config.last_run_status} />
          </div>
        </div>

        {/* Enable toggle */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flexShrink: 0, marginLeft: 12 }}>
          <span style={{ fontSize: 10, color: 'var(--text3)' }}>{config.enabled ? 'On' : 'Off'}</span>
          <div
            onClick={() => onToggle(config.agent_id, !config.enabled)}
            style={{
              width: 32, height: 18, borderRadius: 9, cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
              background: config.enabled ? 'var(--accent2)' : 'var(--border)',
            }}
          >
            <div style={{
              position: 'absolute', top: 3, left: config.enabled ? 17 : 3,
              width: 12, height: 12, borderRadius: '50%', background: 'white',
              transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </div>
        </label>
      </div>

      {/* Description */}
      <p style={{ fontSize: 12, color: 'var(--text3)', margin: '0 0 12px', lineHeight: 1.5 }}>
        {meta.description}
      </p>

      {/* Meta row */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
        <MetaItem label="Schedule" value={scheduleLabel(config.schedule)} />
        {config.last_run_at && (
          <MetaItem
            label="Last run"
            value={formatDistanceToNow(parseISO(config.last_run_at), { addSuffix: true })}
          />
        )}
      </div>

      {/* Last summary */}
      {config.last_run_summary && (
        <div style={{
          fontSize: 11, color: 'var(--text3)', background: 'var(--bg2)',
          borderRadius: 5, padding: '6px 10px', marginBottom: 10, fontFamily: 'monospace',
        }}>
          {config.last_run_summary}
        </div>
      )}

      {/* Run now */}
      {meta.invocable && (
        <button
          onClick={() => onRunNow(config.agent_id, meta.functionName)}
          disabled={!canRun || isRunning}
          style={{
            background: isRunning ? 'var(--bg2)' : 'var(--accent-bg)',
            border: `0.5px solid ${isRunning ? 'var(--border)' : 'var(--accent2)'}`,
            color: isRunning ? 'var(--text3)' : 'var(--accent2)',
            padding: '5px 14px', borderRadius: 5, fontSize: 11, fontWeight: 600,
            cursor: (!canRun || isRunning) ? 'default' : 'pointer',
            fontFamily: 'inherit', opacity: (!canRun || isRunning) ? 0.6 : 1,
            transition: 'all 0.15s',
          }}
        >
          {isRunning ? 'Running…' : 'Run now'}
        </button>
      )}
    </div>
  )
}

function MetaItem({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 11, color: 'var(--text2)', fontWeight: 500 }}>{value}</div>
    </div>
  )
}

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────

export default function AgentDashboard() {
  const { profile } = useAuth()
  const [agents, setAgents]   = useState([])
  const [loading, setLoading] = useState(true)
  const [runningId, setRunningId] = useState(null)
  const { toast, showToast, hideToast } = useToast(3500)

  const fetchAgents = useCallback(async () => {
    const { data } = await supabase
      .from('agent_config')
      .select('*')
      .order('agent_id')
    setAgents(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { fetchAgents() }, [fetchAgents])

  async function handleToggle(agentId, enabled) {
    await supabase
      .from('agent_config')
      .update({ enabled })
      .eq('agent_id', agentId)
    setAgents(prev => prev.map(a => a.agent_id === agentId ? { ...a, enabled } : a))
    showToast(`${AGENT_META[agentId]?.label || agentId} ${enabled ? 'enabled' : 'disabled'}`)
  }

  async function handleRunNow(agentId, functionName) {
    setRunningId(agentId)
    setAgents(prev => prev.map(a => a.agent_id === agentId ? { ...a, last_run_status: 'running' } : a))
    try {
      const { error } = await supabase.functions.invoke(functionName)
      if (error) throw error
      showToast(`${AGENT_META[agentId]?.label || agentId} completed`)
    } catch (err) {
      showToast(`Run failed: ${err.message}`, 'error')
    }
    // Refresh status from DB after a short delay
    setTimeout(async () => {
      await fetchAgents()
      setRunningId(null)
    }, 3000)
  }


  if (profile?.role !== 'admin') {
    return (
      <div style={{ padding: 32, color: 'var(--text3)', fontSize: 14 }}>
        Admin access required.
      </div>
    )
  }

  const activeAgents   = agents.filter(a => a.enabled)
  const inactiveAgents = agents.filter(a => !a.enabled)

  return (
    <div style={{ padding: '24px 28px', maxWidth: 900, margin: '0 auto' }}>

      <Toast {...(toast || {})} onClose={hideToast} />

      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 20 }}>🤖</span>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>AI Agents</h1>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text3)', margin: 0, lineHeight: 1.5 }}>
          Automated agents that monitor your caseload, flag issues, and surface insights.
          All agents run on schedule — use "Run now" to trigger one immediately.
        </p>
      </div>

      {/* Summary bar */}
      {!loading && (
        <div style={{
          display: 'flex', gap: 20, marginBottom: 28, padding: '14px 18px',
          background: 'var(--bg)', border: '0.5px solid var(--border)', borderRadius: 10,
        }}>
          <SummaryItem label="Total agents" value={agents.length} />
          <SummaryItem label="Active" value={activeAgents.length} color="var(--success)" />
          <SummaryItem label="Inactive" value={inactiveAgents.length} color="var(--text3)" />
          <SummaryItem
            label="Last error"
            value={agents.filter(a => a.last_run_status === 'error').length === 0 ? 'None' : agents.filter(a => a.last_run_status === 'error').length}
            color={agents.some(a => a.last_run_status === 'error') ? 'var(--danger)' : 'var(--success)'}
          />
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text3)', fontSize: 13 }}>Loading agents…</div>
      ) : (
        <>
          {/* Active agents */}
          <div style={{ marginBottom: 32 }}>
            <SectionHeader>Active Agents</SectionHeader>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 12 }}>
              {activeAgents.map(a => (
                <AgentCard
                  key={a.agent_id}
                  config={a}
                  onToggle={handleToggle}
                  onRunNow={handleRunNow}
                  runningId={runningId}
                />
              ))}
              {activeAgents.length === 0 && (
                <div style={{ color: 'var(--text3)', fontSize: 12 }}>No active agents.</div>
              )}
            </div>
          </div>

          {/* Inactive agents */}
          {inactiveAgents.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <SectionHeader>Inactive / Deferred</SectionHeader>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 12 }}>
                {inactiveAgents.map(a => (
                  <AgentCard
                    key={a.agent_id}
                    config={a}
                    onToggle={handleToggle}
                    onRunNow={handleRunNow}
                    runningId={runningId}
                  />
                ))}
              </div>
            </div>
          )}

        </>
      )}
    </div>
  )
}

function SectionHeader({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: 'var(--text3)',
      textTransform: 'uppercase', letterSpacing: '0.08em',
      marginBottom: 12, paddingBottom: 6,
      borderBottom: '0.5px solid var(--border)',
    }}>
      {children}
    </div>
  )
}

function SummaryItem({ label, value, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
      <div style={{ fontSize: 22, fontWeight: 600, color: color || 'var(--text)', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
    </div>
  )
}
